import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Flex,
  Text,
  VStack,
  HStack,
  Icon,
  Spinner,
  Button,
  Avatar,
  Heading,
  Tooltip,
  Badge,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  FaTrophy,
  FaMedal,
  FaAward,
  FaChevronRight,
  FaGlobe,
  FaBullseye,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getReviews } from '../services/reviewService';
import { getBusinessImages } from '../services/imageService';
import { logger } from '../utils/logger';

const MotionBox = motion(Box);

/**
 * WebTrafficWidget - Displays top businesses by web traffic/SEO performance
 * Features a podium-style layout for top 3 businesses
 */
const WebTrafficWidget = () => {
  const navigate = useNavigate();
  const [reviewsData, setReviewsData] = useState(null);
  const [businessImages, setBusinessImages] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  // Color mode values
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const subtitleColor = useColorModeValue('gray.500', 'gray.400');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      try {
        // Fetch reviews data which includes traffic data
        const reviewsResponse = await getReviews();
        
        if (mounted) {
          setReviewsData(reviewsResponse);
        }

        // Fetch business images
        const images = await getBusinessImages().catch(err => {
          logger.log('Images not available:', err.message);
          return [];
        });

        if (mounted && images) {
          // Create maps_uri -> image URL mapping
          const imageMap = {};
          images.forEach(img => {
            if (img.maps_uri && img.high_res_image_url_fetched) {
              imageMap[img.maps_uri] = img.high_res_image_url_fetched;
            }
          });
          setBusinessImages(imageMap);
        }
      } catch (error) {
        logger.error('Error fetching Web Traffic data:', error);
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      mounted = false;
    };
  }, []);

  // Get businesses with traffic data sorted by estimated monthly visitors
  const sortedByTraffic = useMemo(() => {
    if (!reviewsData || reviewsData.length === 0) return [];
    return [...reviewsData]
      .filter(item => item.traffic_data?.metrics?.estimated_monthly_visitors > 0)
      .sort((a, b) => 
        (b.traffic_data?.metrics?.estimated_monthly_visitors || 0) - 
        (a.traffic_data?.metrics?.estimated_monthly_visitors || 0)
      );
  }, [reviewsData]);

  // Get top 3 for podium
  const top3Businesses = useMemo(() => {
    return sortedByTraffic.slice(0, 3);
  }, [sortedByTraffic]);

  // Calculate market total
  const totalMarketTraffic = useMemo(() => {
    return sortedByTraffic.reduce((sum, b) => sum + (b.traffic_data?.metrics?.estimated_monthly_visitors || 0), 0);
  }, [sortedByTraffic]);

  const handleViewDetails = () => {
    navigate('/dashboards/web-traffic');
  };

  // Helper to format traffic numbers
  const formatTraffic = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toLocaleString() ?? '0';
  };

  // Loading state
  if (isLoading) {
    return (
      <Box
        bg={cardBg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="2xl"
        p={6}
        boxShadow="sm"
      >
        <Flex align="center" justify="center" minH="300px">
          <VStack spacing={3}>
            <Spinner size="lg" color="teal.500" thickness="3px" />
            <Text color={subtitleColor} fontSize="sm">Loading traffic data...</Text>
          </VStack>
        </Flex>
      </Box>
    );
  }

  // No data state
  if (!reviewsData || sortedByTraffic.length === 0) {
    return (
      <Box
        bg={cardBg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="2xl"
        p={6}
        boxShadow="sm"
      >
        <VStack spacing={3} py={8}>
          <Icon as={FaGlobe} boxSize={10} color="gray.300" />
          <Text color={subtitleColor}>No traffic data available</Text>
        </VStack>
      </Box>
    );
  }

  return (
    <MotionBox
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      bg={cardBg}
      borderRadius="2xl"
      borderWidth="1px"
      borderColor={borderColor}
      p={6}
      boxShadow="sm"
    >
      {/* Header */}
      <Flex justify="space-between" align="center" mb={6}>
        <HStack spacing={3}>
          <Icon as={FaGlobe} color="teal.500" boxSize={6} />
          <Heading size="md" textTransform="uppercase" fontStyle="italic" fontSize="1.3em">
            Traffic Leaders
          </Heading>
        </HStack>
        <Button
          size="sm"
          colorScheme="teal"
          variant="ghost"
          rightIcon={<FaChevronRight />}
          onClick={handleViewDetails}
        >
          View Full Dashboard
        </Button>
      </Flex>

      {/* Market Summary */}
      <HStack spacing={4} mb={6} justify="center">
        <VStack spacing={0}>
          <Text fontSize="2xl" fontWeight="bold" color="teal.600">
            {formatTraffic(totalMarketTraffic)}
          </Text>
          <Text fontSize="xs" color={subtitleColor} textTransform="uppercase">
            Total Backlinks
          </Text>
        </VStack>
        <Box h="40px" w="1px" bg={borderColor} />
        <VStack spacing={0}>
          <Text fontSize="2xl" fontWeight="bold" color="teal.600">
            {sortedByTraffic.length}
          </Text>
          <Text fontSize="xs" color={subtitleColor} textTransform="uppercase">
            With Traffic Data
          </Text>
        </VStack>
      </HStack>

      {/* Top 3 Podium */}
      {top3Businesses.length >= 3 && (
        <Box mb={4}>
          <Flex justify="center" align="flex-end" gap={4} w="full">
            {/* Silver - Second Place */}
            <Box
              bg={cardBg}
              borderRadius="xl"
              textAlign="center"
              position="relative"
              transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
              _hover={{ 
                transform: 'translateY(-4px)', 
                shadow: 'xl',
              }}
              overflow="hidden"
              flex="1"
              maxW="200px"
              boxShadow="lg"
              borderWidth="1px"
              borderColor={borderColor}
            >
              <Box h="3px" bgGradient="linear(to-r, gray.300, gray.500, gray.300)" />
              <VStack p={4} spacing={3}>
                <Flex
                  position="absolute"
                  top={3}
                  left={3}
                  w="24px"
                  h="24px"
                  borderRadius="full"
                  bgGradient="linear(to-br, gray.300, gray.500)"
                  justify="center"
                  align="center"
                  shadow="md"
                >
                  <Text color="white" fontWeight="bold" fontSize="xs">2</Text>
                </Flex>
                
                <Icon as={FaMedal} color="gray.400" boxSize={5} />
                
                <Box p="2px" borderRadius="full" bgGradient="linear(to-br, gray.300, gray.500)">
                  <Avatar 
                    name={top3Businesses[1]?.name} 
                    src={businessImages[top3Businesses[1]?.maps_uri] || ''} 
                    bg="gray.100" 
                    boxSize="50px"
                    borderWidth="2px"
                    borderColor={cardBg}
                  />
                </Box>
                
                <VStack spacing={0}>
                  <Tooltip label={top3Businesses[1]?.name} placement="top" hasArrow openDelay={300}>
                    <Text fontSize="sm" fontWeight="bold" noOfLines={1} color={textColor}>
                      {top3Businesses[1]?.name}
                    </Text>
                  </Tooltip>
                  <Text 
                    fontWeight="extrabold" 
                    fontSize="xl" 
                    bgGradient="linear(to-r, gray.600, gray.800)"
                    bgClip="text"
                  >
                    {formatTraffic(top3Businesses[1]?.traffic_data?.metrics?.estimated_monthly_visitors)}
                  </Text>
                  <Text fontSize="2xs" color={subtitleColor} fontWeight="medium" textTransform="uppercase" letterSpacing="wider">
                    backlinks
                  </Text>
                </VStack>
                
                {top3Businesses[1]?.query_ranking_info?.url_ranking && (
                  <Badge colorScheme="gray" fontSize="2xs">
                    Rank #{top3Businesses[1].query_ranking_info.url_ranking}
                  </Badge>
                )}
                
                <Box w="full" px={2}>
                  <Box h="4px" bg="gray.100" borderRadius="full" overflow="hidden">
                    <Box
                      h="full"
                      w={`${((top3Businesses[1]?.traffic_data?.metrics?.estimated_monthly_visitors || 0) / (top3Businesses[0]?.traffic_data?.metrics?.estimated_monthly_visitors || 1)) * 100}%`}
                      bgGradient="linear(to-r, gray.400, gray.500)"
                      borderRadius="full"
                      transition="width 0.5s ease-out"
                    />
                  </Box>
                </Box>
              </VStack>
            </Box>

            {/* Gold - First Place */}
            <Box
              bg={cardBg}
              borderRadius="xl"
              textAlign="center"
              position="relative"
              transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
              _hover={{ 
                transform: 'translateY(-8px) scale(1.02)', 
                shadow: '0 20px 40px -12px rgba(56, 178, 172, 0.4)',
              }}
              overflow="hidden"
              flex="1.1"
              maxW="220px"
              boxShadow="0 15px 30px -10px rgba(56, 178, 172, 0.3)"
              zIndex={2}
              mb={4}
              borderWidth="1px"
              borderColor="teal.200"
            >
              <Box
                h="4px"
                bgGradient="linear(to-r, teal.300, cyan.400, teal.400, cyan.400, teal.300)"
                backgroundSize="200% 100%"
                animation="shimmer 2s linear infinite"
                sx={{
                  '@keyframes shimmer': {
                    '0%': { backgroundPosition: '200% 0' },
                    '100%': { backgroundPosition: '-200% 0' },
                  },
                }}
              />
              <VStack p={5} spacing={3}>
                <Flex
                  position="absolute"
                  top={3}
                  left={3}
                  w="28px"
                  h="28px"
                  borderRadius="full"
                  bgGradient="linear(to-br, teal.300, cyan.400)"
                  justify="center"
                  align="center"
                  shadow="lg"
                >
                  <Text color="white" fontWeight="bold" fontSize="sm">1</Text>
                </Flex>
                
                <Icon as={FaTrophy} color="teal.400" boxSize={7} filter="drop-shadow(0 2px 4px rgba(56, 178, 172, 0.5))" />
                
                <Box p="3px" borderRadius="full" bgGradient="linear(to-br, teal.300, cyan.400, teal.500)" shadow="lg">
                  <Avatar 
                    name={top3Businesses[0]?.name} 
                    src={businessImages[top3Businesses[0]?.maps_uri] || ''} 
                    bg="teal.50" 
                    boxSize="65px"
                    borderWidth="3px"
                    borderColor={cardBg}
                  />
                </Box>
                
                <VStack spacing={0}>
                  <Tooltip label={top3Businesses[0]?.name} placement="top" hasArrow openDelay={300}>
                    <Text fontSize="md" fontWeight="bold" noOfLines={1} color={textColor}>
                      {top3Businesses[0]?.name}
                    </Text>
                  </Tooltip>
                  <Text 
                    fontWeight="extrabold" 
                    fontSize="2xl" 
                    bgGradient="linear(to-r, teal.500, cyan.500)"
                    bgClip="text"
                  >
                    {formatTraffic(top3Businesses[0]?.traffic_data?.metrics?.estimated_monthly_visitors)}
                  </Text>
                  <Text fontSize="2xs" color={subtitleColor} fontWeight="medium" textTransform="uppercase" letterSpacing="wider">
                    backlinks
                  </Text>
                </VStack>
                
                {top3Businesses[0]?.query_ranking_info?.url_ranking && (
                  <Badge colorScheme="teal" fontSize="2xs">
                    Rank #{top3Businesses[0].query_ranking_info.url_ranking}
                  </Badge>
                )}
                
                <Box w="full" px={2}>
                  <Box h="5px" bg="teal.50" borderRadius="full" overflow="hidden">
                    <Box h="full" w="100%" bgGradient="linear(to-r, teal.400, cyan.400)" borderRadius="full" />
                  </Box>
                </Box>
              </VStack>
            </Box>

            {/* Bronze - Third Place */}
            <Box
              bg={cardBg}
              borderRadius="xl"
              textAlign="center"
              position="relative"
              transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
              _hover={{ 
                transform: 'translateY(-4px)', 
                shadow: 'xl',
              }}
              overflow="hidden"
              flex="1"
              maxW="200px"
              boxShadow="lg"
              borderWidth="1px"
              borderColor={borderColor}
            >
              <Box h="3px" bgGradient="linear(to-r, orange.200, orange.400, orange.200)" />
              <VStack p={4} spacing={3}>
                <Flex
                  position="absolute"
                  top={3}
                  left={3}
                  w="24px"
                  h="24px"
                  borderRadius="full"
                  bgGradient="linear(to-br, orange.300, orange.500)"
                  justify="center"
                  align="center"
                  shadow="md"
                >
                  <Text color="white" fontWeight="bold" fontSize="xs">3</Text>
                </Flex>
                
                <Icon as={FaAward} color="orange.400" boxSize={5} />
                
                <Box p="2px" borderRadius="full" bgGradient="linear(to-br, orange.300, orange.500)">
                  <Avatar 
                    name={top3Businesses[2]?.name} 
                    src={businessImages[top3Businesses[2]?.maps_uri] || ''} 
                    bg="orange.50" 
                    boxSize="50px"
                    borderWidth="2px"
                    borderColor={cardBg}
                  />
                </Box>
                
                <VStack spacing={0}>
                  <Tooltip label={top3Businesses[2]?.name} placement="top" hasArrow openDelay={300}>
                    <Text fontSize="sm" fontWeight="bold" noOfLines={1} color={textColor}>
                      {top3Businesses[2]?.name}
                    </Text>
                  </Tooltip>
                  <Text 
                    fontWeight="extrabold" 
                    fontSize="xl" 
                    bgGradient="linear(to-r, orange.500, orange.600)"
                    bgClip="text"
                  >
                    {formatTraffic(top3Businesses[2]?.traffic_data?.metrics?.estimated_monthly_visitors)}
                  </Text>
                  <Text fontSize="2xs" color={subtitleColor} fontWeight="medium" textTransform="uppercase" letterSpacing="wider">
                    backlinks
                  </Text>
                </VStack>
                
                {top3Businesses[2]?.query_ranking_info?.url_ranking && (
                  <Badge colorScheme="orange" fontSize="2xs">
                    Rank #{top3Businesses[2].query_ranking_info.url_ranking}
                  </Badge>
                )}
                
                <Box w="full" px={2}>
                  <Box h="4px" bg="orange.50" borderRadius="full" overflow="hidden">
                    <Box
                      h="full"
                      w={`${((top3Businesses[2]?.traffic_data?.metrics?.estimated_monthly_visitors || 0) / (top3Businesses[0]?.traffic_data?.metrics?.estimated_monthly_visitors || 1)) * 100}%`}
                      bgGradient="linear(to-r, orange.300, orange.500)"
                      borderRadius="full"
                      transition="width 0.5s ease-out"
                    />
                  </Box>
                </Box>
              </VStack>
            </Box>
          </Flex>
        </Box>
      )}

      {/* Runner-ups list (4th and 5th place) */}
      {sortedByTraffic.length > 3 && (
        <Box mt={4} pt={4} borderTopWidth="1px" borderColor={borderColor}>
          <Text fontSize="xs" fontWeight="semibold" color={subtitleColor} mb={3} textTransform="uppercase">
            Runner-ups
          </Text>
          <VStack spacing={2} align="stretch">
            {sortedByTraffic.slice(3, 5).map((business, idx) => (
              <Flex
                key={business.maps_uri}
                align="center"
                justify="space-between"
                p={2}
                borderRadius="md"
                _hover={{ bg: hoverBg }}
              >
                <HStack spacing={3}>
                  <Flex
                    w="24px"
                    h="24px"
                    borderRadius="full"
                    bg="gray.100"
                    justify="center"
                    align="center"
                    fontWeight="bold"
                    fontSize="xs"
                    color="gray.600"
                  >
                    {idx + 4}
                  </Flex>
                  <Avatar 
                    name={business.name} 
                    src={businessImages[business.maps_uri] || ''} 
                    size="sm"
                  />
                  <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
                    {business.name}
                  </Text>
                </HStack>
                <HStack spacing={2}>
                  {business.query_ranking_info?.url_ranking && (
                    <Badge colorScheme="gray" fontSize="2xs">
                      #{business.query_ranking_info.url_ranking}
                    </Badge>
                  )}
                  <Text fontSize="sm" fontWeight="bold" color="teal.600">
                    {formatTraffic(business.traffic_data?.metrics?.estimated_monthly_visitors)}
                  </Text>
                </HStack>
              </Flex>
            ))}
          </VStack>
        </Box>
      )}
    </MotionBox>
  );
};

export default WebTrafficWidget;
